import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AuthLayout, AuthStatus } from '../components/AuthLayout';
import { InlineError } from '../components/Feedback';
import { signup } from '../lib/endpoints';
import { errorMessage } from '../lib/api';
import { useToast } from '../lib/toast';

/** Seconds between resends. Starts armed on mount because a link was just sent. */
const RESEND_COOLDOWN_SECONDS = 60;

const DEFAULT_LINK_HOURS = 24;

export function CheckEmailPage(): JSX.Element {
  const [params] = useSearchParams();
  const toast = useToast();

  const address = (params.get('email') ?? '').trim();
  const hoursParam = Number(params.get('hours'));
  const [linkHours, setLinkHours] = useState(
    Number.isFinite(hoursParam) && hoursParam > 0 ? Math.round(hoursParam) : DEFAULT_LINK_HOURS,
  );

  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resentNote, setResentNote] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  if (address === '') {
    return (
      <AuthLayout
        title="Check your email"
        subtitle="We could not tell which address to use."
        footer={
          <>
            <Link to="/signup">Start again</Link>
            <Link to="/signin">Sign in</Link>
          </>
        }
      >
        <AuthStatus tone="warn" title="No email address in the link">
          <p>
            Open the confirmation email we sent, or start the sign-up again and we will send a fresh
            link.
          </p>
        </AuthStatus>
      </AuthLayout>
    );
  }

  const resend = async () => {
    setPending(true);
    setError(null);
    try {
      const response = await signup.resend(address);
      if (response?.linkValidForHours) setLinkHours(response.linkValidForHours);
      // The API is deliberately vague about whether the address is registered; so are we.
      setResentNote(
        response?.message ??
          'If that address needs a confirmation link, another one is on its way.',
      );
      toast.success('Email sent', 'Check your inbox, and your spam folder.');
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (cause) {
      const message = errorMessage(cause);
      setError(message);
      toast.error('Could not resend the email', message);
    } finally {
      setPending(false);
    }
  };

  return (
    <AuthLayout
      title="Check your email"
      subtitle="One click and your account is live."
      footer={
        <>
          <Link to="/signup">Use a different address</Link>
          <Link to="/signin">Sign in</Link>
        </>
      }
      note="Nothing is created until you confirm the address, so you can safely start again."
    >
      <AuthStatus tone="info" title={`We’ve emailed a link to ${address}`}>
        <p>
          The link is valid for {linkHours} hour{linkHours === 1 ? '' : 's'}. Open it on this device
          if you can — it signs you straight in.
        </p>
        <p className="auth-status-hint">
          No email after a minute or two? Check your spam or junk folder, and confirm your mail
          system is not holding messages from unknown senders.
        </p>
      </AuthStatus>

      {resentNote ? (
        <p className="auth-neutral-note" role="status">
          {resentNote}
        </p>
      ) : null}

      <InlineError message={error} />

      <button
        type="button"
        className="btn btn-secondary btn-block"
        onClick={() => void resend()}
        disabled={pending || cooldown > 0}
      >
        {pending
          ? 'Sending…'
          : cooldown > 0
            ? `Resend email in ${cooldown}s`
            : 'Resend email'}
      </button>
    </AuthLayout>
  );
}
